import { motion } from "framer-motion";
import { Sparkles, Cloud, LineChart, GraduationCap, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const EnterprisePlatform = () => (
    <section data-testid="platform-showcase-section" className="sxe-section relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
            <div className="hero-orb w-[520px] h-[520px] bg-[#7B00CC]/12 top-20 -right-40" />
        </div>
        <div className="sxe-container relative">
            <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20 items-center">
                <div>
                    <div className="overline"><span className="dot" /> Enterprise Learning Platform</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                        A single platform for{" "}
                        <span className="text-gradient">enterprise capability development</span>.
                    </h2>
                    <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6 max-w-xl">
                        SpringXE consolidates learning, labs, assessments, coaching, and analytics into one AI-native experience — so L&D, HR, and business leaders can align on a single source of truth for capability.
                    </p>

                    <div className="mt-8 grid grid-cols-2 gap-4 max-w-lg">
                        {[
                            { k: "Modules curated", v: "10K+", i: GraduationCap },
                            { k: "Cloud lab hours", v: "24M+", i: Cloud },
                            { k: "Skill signals", v: "180M+", i: LineChart },
                            { k: "AI copilots", v: "24/7", i: Sparkles },
                        ].map((s) => (
                            <div key={s.k} className="sxe-card p-4 flex items-center gap-3">
                                <s.i className="w-5 h-5 text-[#C266FF] shrink-0" />
                                <div>
                                    <div className="big-num text-xl">{s.v}</div>
                                    <div className="text-[11px] uppercase tracking-widest text-[#75668C] font-display">{s.k}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-9 flex flex-wrap gap-3">
                        <Link to="/products" data-testid="showcase-cta-explore" className="btn-primary">
                            Explore Platform <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <a href="#contact" data-testid="showcase-cta-experts" className="btn-secondary">
                            Talk to Our Experts
                        </a>
                    </div>
                </div>

                {/* Right visual: layered cards */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="relative min-h-[440px]"
                >
                    <div className="absolute inset-0 hero-glow" />

                    {/* Overlay card 1 */}
                    <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -left-2 md:-left-6 bottom-6 sxe-glass rounded-2xl border border-white/[0.06] p-4 w-56"
                    >
                        <div className="text-[10px] uppercase tracking-widest text-[#75668C] font-display">Cohort · Active</div>
                        <div className="font-display text-white text-sm font-medium mt-1 tracking-tight">Cloud & Data Bootcamp</div>
                        <div className="mt-3 flex items-center gap-2">
                            <div className="flex -space-x-2">
                                {["A", "R", "V", "S"].map((c, i) => (
                                    <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center text-white text-[10px] font-display font-semibold border border-[#0a041a]">
                                        {c}
                                    </div>
                                ))}
                            </div>
                            <span className="text-[11px] text-[#B0A3C4]">+ 214 more</span>
                        </div>
                    </motion.div>

                    {/* Overlay card 2 */}
                    <motion.div
                        animate={{ y: [0, 12, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute right-0 bottom-2 sxe-glass rounded-2xl border border-white/[0.06] p-4 w-52"
                    >
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#C266FF]" />
                            <div className="text-[10px] uppercase tracking-widest text-[#75668C] font-display">Xelerate</div>
                        </div>
                        <div className="text-white text-sm font-display font-medium mt-1">Next best learning path</div>
                        <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                            <div className="h-full w-[68%] bg-gradient-to-r from-[#7B00CC] to-[#C266FF]" />
                        </div>
                        <div className="text-[10px] text-[#75668C] font-mono mt-1">68% recommended</div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    </section>
);

export default EnterprisePlatform;
